import type { ReactNode } from "react";
import { useState } from "react";

import { cn } from "@btnet/utilities-core";

export interface IShellNavItem {
  key: string;
  label: string;
  icon?: ReactNode;
}

export interface IShellSlotProps {
  navItems: IShellNavItem[];
  selectedKey: string;
  onSelectKey: (key: string) => void;
}

export interface IShellProps {
  /**
   * Left nav slot. Receives the shell's shared selection state so the nav can render/react to
   * it. Optional — omit (or pass `undefined`) to not render a sidebar, e.g. at a breakpoint
   * where the caller shows `renderBottombar` instead.
   */
  renderSidebar?: (slotProps: IShellSlotProps) => ReactNode;
  /** Main canvas slot. */
  children: ReactNode;
  /**
   * Bottom nav slot. Optional — omit (or pass `undefined`) to not render a bottombar. Unlike
   * `renderSidebar`/`renderBottombar` in an earlier version of this component, BTShellLayout has
   * no breakpoint logic of its own: which slot to populate at a given width is entirely up to
   * the caller (see `BTShellLayout.stories.tsx`'s `WithNav` for the `useMediaQuery` pattern).
   */
  renderBottombar?: (slotProps: IShellSlotProps) => ReactNode;
  className?: string;
  "data-testid": string;
}

const DUMMY_NAV_ITEM_COUNT = 20;

const DUMMY_NAV_ITEMS: IShellNavItem[] = Array.from(
  { length: DUMMY_NAV_ITEM_COUNT },
  (_, i) => ({
    key: `item${i}`,
    label: `Item ${i + 1}`,
  }),
);

/**
 * Top-level layout shell for the research-preview information-architecture nav
 * (ado-285671, ado-286232). The canvas is `children`; `renderSidebar`/`renderBottombar` are
 * optional render-prop slots the caller populates conditionally (e.g. by breakpoint — see
 * `WithNav` in the stories file). Owns `selectedKey` and `navItems` as shared state and hands
 * both, plus a setter, into `renderSidebar`/`renderBottombar` so a single source of truth can
 * drive nav selection without a shared parent threading props between sibling ReactNodes.
 *
 * `navItems` is dummy data for now (`item0`-`item19`); a future story swaps
 * this for a real nav API response without changing the slot contract.
 */
export function BTShellLayout({
  renderSidebar,
  renderBottombar,
  children,
  className,
  "data-testid": testid,
}: IShellProps) {
  const [selectedKey, setSelectedKey] = useState(DUMMY_NAV_ITEMS[0]!.key);

  const slotProps: IShellSlotProps = {
    navItems: DUMMY_NAV_ITEMS,
    selectedKey,
    onSelectKey: setSelectedKey,
  };

  return (
    <div
      data-testid={testid}
      className={cn("flex h-dvh w-full overflow-hidden bg-white", className)}
    >
      {renderSidebar?.(slotProps)}
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex flex-1 flex-col overflow-hidden">{children}</div>
        {renderBottombar?.(slotProps)}
      </div>
    </div>
  );
}
